import { ago, until } from '../../../nucleus/format';
import { STATUS_WORDS, statusWord, statusTone } from '../../../components/nucleus/status';
import { pluginsApi, autosApi, runsApi } from './api';
import {
    PLUGIN_STATES, needWord, stuckWord, fieldLabels, isSettingUp, scheduleWord, scheduleWhy, outcomeOf, startedByWord, startedByKind, refusedLine,
    leftOutOf, LEFT_OUT_WORD, consentWord, accessSummary, accessWord, automationWord, listWords, agentWord,
} from './words';

// THE SHAPES THE SECTION READS, made from the server's answers. The lifecycle
// is the server's and is read, never worked out here from flags: which step a
// plugin still needs, each tool's needs and its one schedule. Where a field is
// one an older server does not send, the shape says null rather than guessing.

export const firstSentence = (s) => String(s || '').split(/(?<=[.!?])\s/)[0] || undefined;

const latest = (list) => list.filter(Boolean).sort().pop() || null;
const soonest = (list) => list.filter(Boolean).sort()[0] || null;
// a schedule that wants a hand: it is on, and something stops it
const TROUBLED = ['failing', 'paused', 'needs_realm'];
const num = (v) => (typeof v === 'number' ? v : null);

/** What approval stands at: { approvedAt, approvedBy, changed, changes } or null (an older server). */
export function approvalOf(source) {
    const a = source?.approval;
    if (!a || typeof a !== 'object') return null;
    return {
        approvedAt: a.approved_at || null,
        approvedBy: a.approved_by?.name || null,
        approvedVersion: a.approved_version ?? null,
        currentVersion: a.current_version ?? null,
        changed: !!a.changed,
        changes: (a.changes || []).map((c) => ({
            part: c.part, toolId: c.tool_id ?? null, added: c.added || [], removed: c.removed || [],
        })),
    };
}

/** One plugin as the section reads it: the listing's row and its lifecycle here. */
export function pluginItem(p, lifecycle) {
    const lc = lifecycle || null;
    const tools = lc?.tools || [];
    const schedules = tools.map((t) => ({ ...(t.schedule || { state: 'off' }), toolId: t.tool_id, tool: t.name }));
    const state = lc?.state || null;
    const installed = !!lc?.installed;
    const paused = state === 'paused';
    const last = latest(schedules.map((s) => s.last_run_at));
    const troubled = schedules.filter((s) => TROUBLED.includes(s.state));
    return {
        id: p.id,
        name: p.display_name || p.name || 'Plugin',
        blurb: p.description || '',
        plugin: p,
        lifecycle: lc,
        state,
        installed,
        paused,
        // Setting up: installed, and a step still open (turning it on included)
        settingUp: installed && isSettingUp(state),
        needs: lc?.needs || [],
        tools,
        schedules,
        // switched on and meant to run (running or failing)
        running: schedules.filter((s) => s.state === 'running' || s.state === 'failing').length,
        failing: schedules.filter((s) => s.state === 'failing').length,
        troubled,
        lastRun: last,
        lastFailed: !!last && schedules.some((s) => s.last_run_at === last && s.last_status === 'failed'),
        next: soonest(schedules.map((s) => s.next_run_at)),
        approval: approvalOf(lc),
        // runs parked for a yes, when the server counts them
        waiting: num(lc?.waiting) ?? num(lc?.requests_pending) ?? num(p.waiting_count),
        // the store's facts (an older listing sends fewer)
        logo: p.logo_url || null,
        verified: !!p.is_verified,
        madeBy: p.made_by || p.developer_name || p.developer_email || null,
        toolsCount: num(p.tools_count) ?? (p.tools || []).filter((t) => t?.tool_id).length,
        effects: p.effects_summary ? { count: p.effects_summary.count || 0, irreversible: p.effects_summary.irreversible || 0 } : null,
        updated: p.updated_at || null,
        listed: p.created_at || null,
        website: p.website_url || null,
        privacyPolicy: p.privacy_policy_url || null,
        // NEEDS YOU: installed here, not Off, and still Setting up or with a
        // tool that can't run. Not set up is the store's, not a want.
        needsYou: installed && !paused && (state !== 'ready' || troubled.length > 0),
    };
}

/** Everything a plugin still needs here: its steps, then its stuck schedules. */
export function needsOf(item) {
    const steps = (item.needs || []).filter((n) => n.kind !== 'install').map((n) => needWord(n, item.lifecycle?.approval));
    if (item.paused) return steps;
    const stuck = (item.troubled || []).map(stuckWord);
    return [...steps, ...stuck];
}

/** The one thing to do next for a plugin, as an instruction (null when nothing waits on you). */
export const nextStep = (item) => (item?.paused ? null : needsOf(item || {})[0] || null);

// THE ONE ACT A PLUGIN WANTS NEXT, by its state: the inspector's primary.
// Off still runs when you run it, so its primary is Run as Ready's is.
const PRIMARY = {
    not_installed: 'install', paused: 'run', needs_connection: 'connect', needs_credentials: 'credentials',
    needs_parameters: 'settings', needs_approval: 'approve', ready: 'run',
};
export const primaryOf = (lifecycle) => PRIMARY[lifecycle?.state] || 'install';

// the lifecycle's states in the order a plugin moves through them
export const STATE_ORDER = PLUGIN_STATES;

/** What a declaration writes, as { verb, what } pairs. */
export function writesOf(decl) {
    return (decl || []).filter(Boolean).map((d) => ({
        verb: d.action || 'create',
        what: d.schema_name || d.name || (d.schema_id === '__freeform__' ? 'any schema' : d.schema_id) || 'nodes',
    }));
}

/** A plugin's tools as one list: the lifecycle's (each with its needs and its
 *  schedule) over what the plugin declares (description, writes) and how the
 *  setup answer describes each (effects, mode). A plugin with no tools is one
 *  tool, '' — itself. */
export function toolsOf(item, states = []) {
    const declared = (item.plugin?.tools || []).filter((t) => t && t.tool_id);
    const lc = item.lifecycle?.tools || [];
    const byId = (id) => declared.find((t) => String(t.tool_id) === String(id)) || null;
    const stateOf = (id) => (states || []).find((s) => String(s.tool_id ?? '') === String(id ?? '')) || null;
    const ids = lc.length ? lc.map((t) => t.tool_id) : declared.length ? declared.map((t) => String(t.tool_id)) : [''];
    return ids.map((id) => {
        const life = lc.find((t) => t.tool_id === id) || null;
        const decl = id ? byId(id) : null;
        const st = stateOf(id);
        return {
            id,
            name: life?.name || decl?.name || st?.name || id || item.name,
            description: decl?.description || st?.description || (id ? '' : item.blurb),
            runnable: life ? !!life.runnable : false,
            needs: life?.needs || [],
            schedule: life?.schedule || { state: 'off' },
            writes: writesOf(decl?.output_declaration?.length ? decl.output_declaration : (id ? [] : item.plugin?.output_declaration)),
            effects: st?.effects || [],
            irreversible: !!st?.irreversible,
            mode: st?.mode || null,
        };
    });
}

// WHY A RUN WAS REFUSED, plainly: the runner's code, said for a person.
// `labelOf` names a field by its label (the run form's) rather than its key.
export function refusalOf(e, labelOf = (k) => k) {
    const info = e?.info || {};
    const named = (keys) => listWords(fieldLabels(keys).map(labelOf));
    switch (e?.code) {
        case 'NOT_INSTALLED': return 'It is not set up here. Set it up first.';
        case 'PLUGIN_PAUSED': return 'It is off here. Turn it on first.';
        case 'GRANT_DOES_NOT_ALLOW_WRITES': {
            const what = (info.writes || []).join(', ');
            return `The realm you chose does not let it ${what || 'write what it writes'}. Choose another realm.`;
        }
        case 'NOT_CONFIGURED': {
            const what = named(info.missing_fields);
            return what ? `It needs ${what} first.` : 'A setting it needs has no value.';
        }
        case 'VALIDATION_ERROR': {
            // a field this run answers, left empty
            const what = named(info.missing_fields);
            return what ? `This run needs a value for ${what}.` : e?.message || 'A value it was given is not valid.';
        }
        case 'PRIVACY_REALM_REQUIRED': return 'Choose a realm it runs in.';
        case 'TOOL_ID_REQUIRED': return 'Choose which tool to run.';
        case 'NO_SIGNING_SECRET': return 'Its developer has not finished setting it up.';
        default: return e?.message || 'The run failed.';
    }
}

// WHAT A RUN LEFT OUT (receipt.outputs_dropped): the words are words.js's
export const DROP_WORD = LEFT_OUT_WORD;
export const droppedOf = leftOutOf;

/** What a realm does not let a tool write (`cannot_write`: ['create notes', 'edit']), as a sentence. */
export function cannotWriteWord(cannotWrite, whose = 'Its realm') {
    const what = (cannotWrite || []).filter(Boolean);
    return what.length ? `${whose} does not let it ${what.join(', ')}` : null;
}

// WHAT A PLUGIN READS, for its approval: what the server says it approves
// (setup-config `reads`, as consent_record.spec_of holds it, or a list), else
// the plugin's own declaration — its node requirements and each tool's. One
// entry a schema: { schemaId } or { any: true }.
function readEntries(list) {
    const out = [];
    (list || []).forEach((r) => {
        if (Array.isArray(r)) { out.push({ type: r[0], schema_id: r[1] }); return; }
        if (!r || typeof r !== 'object') return;
        if (r.resource && r.resource !== 'nodes') return; // not a node read
        if (r.verb && r.verb !== 'read') return;
        const ids = r.schema_ids?.length ? r.schema_ids : [r.schema_id || null];
        ids.forEach((id) => out.push({ type: r.type || 'schema', schema_id: id, schema_name: r.schema_name || r.name || null }));
    });
    return out;
}
export function readsOf(setup, plugin) {
    const spec = setup?.reads;
    let raw;
    if (Array.isArray(spec)) raw = readEntries(spec);
    else if (spec && typeof spec === 'object') raw = [...readEntries(spec.plugin), ...Object.values(spec.tools || {}).flatMap(readEntries)];
    else {
        raw = [
            ...readEntries(plugin?.node_requirements || plugin?.dlr_inputs),
            ...(plugin?.tools || []).flatMap((t) => readEntries(t?.node_requirements)),
        ];
    }
    const seen = new Set();
    const out = [];
    raw.forEach((r) => {
        // a step's upstream is wiring, not a read of the graph
        if (r.type === 'plugin_output' || r.type === 'upstream') return;
        const any = r.type === 'any' || !r.schema_id;
        const key = any ? '*' : String(r.schema_id);
        if (seen.has(key)) return;
        seen.add(key);
        out.push(any ? { key, any: true } : { key, schemaId: String(r.schema_id), name: r.schema_name || null });
    });
    return out;
}

/** What else the approval covers: deleting what it reads, asking every run, the request it makes. */
export function approvalTermsOf(setup, plugin) {
    const pick = (k) => (setup && setup[k] !== undefined ? setup[k] : plugin?.[k]);
    let request = setup?.request || null;
    if (!request && plugin?.execution_type === 'request' && plugin?.request_spec?.url) {
        let path = '/';
        try { path = new URL(plugin.request_spec.url).pathname || '/'; } catch { path = '/'; }
        request = { method: String(plugin.request_spec.method || 'GET').toUpperCase(), path };
    }
    return { removeSource: pick('remove_source') === true, requiresConsent: pick('requires_consent') === true, request };
}

// ── automations and flows ────────────────────────────────────────────────────

/** How an automation stands, one way: a tool that runs automatically by its
 *  lifecycle state, any other by its own; both said On · Off · Can't run · Keeps failing. */
function standingOf(j, active, failed) {
    if (j.tool_schedule && j.schedule?.state) {
        const s = j.schedule;
        return { of: 'schedule', state: s.state, word: statusWord(s.state, 'schedule'), tone: statusTone(s.state), why: scheduleWhy(s) };
    }
    const state = ['on', 'off', 'failing'].includes(j.state) ? j.state : !active ? 'off' : failed ? 'failing' : 'on';
    return { of: 'automation', state, word: automationWord(state), tone: statusTone(state), why: null };
}

/** One automation: an event or manual trigger, or a tool's schedule. `state`
 *  is the older running · failing · paused (the current Automations page's
 *  sets); `status` is the one the lifecycle agrees with. */
export function automationItem(j) {
    const lastStatus = j.last_run_status || null;
    const failed = lastStatus === 'failed' || lastStatus === 'error';
    const active = !!j.is_active;
    return {
        id: j.id,
        name: j.name || 'Automation',
        blurb: j.description || null,
        trigger: j.trigger_type || 'manual',
        active,
        state: !active ? 'paused' : failed ? 'failing' : 'running',
        status: standingOf(j, active, failed),
        toolSchedule: !!j.tool_schedule,
        schedule: j.schedule || null,
        lastRun: j.last_run_at || null,
        lastStatus,
        lastError: j.last_run_error || null,
        next: j.next_run_at || null,
        plugin: j.plugin?.name || null,
        pluginId: j.plugin_id || j.plugin?.id || null,
        toolId: j.trigger_config?.tool_id || null,
        failed,
        cron: j.trigger_config?.cron || null,
        event: j.trigger_config?.event || null,
        schemaId: j.trigger_config?.schema_id || null,
        created: j.created_at || null,
        pluginConfig: j.plugin_config || null,
        appConfig: j.action_config || null,
        triggerConfig: j.trigger_config || {},
        // the app that made it, when an app did (only ofSelf stamps this)
        createdByAppId: j.created_by_app_id || j.trigger_config?.created_by_app_id || null,
        createdByApp: j.created_by_app_name || j.created_by_app?.name || null,
    };
}

/** A flow's members in the order they run: follow the derived edges from the roots. */
export function orderedChain(members = [], edges = []) {
    if (!members.length || !edges.length) return members;
    const incoming = new Set(edges.map((e) => e.to));
    const next = {}; edges.forEach((e) => { next[e.from] = e.to; });
    const byId = {}; members.forEach((m) => { byId[m.automation_id] = m; });
    const roots = members.filter((m) => !incoming.has(m.automation_id));
    const out = []; const seen = new Set();
    (roots.length ? roots : [members[0]]).forEach((r) => {
        let cur = r.automation_id;
        while (cur && byId[cur] && !seen.has(cur)) { seen.add(cur); out.push(byId[cur]); cur = next[cur]; }
    });
    members.forEach((m) => { if (!seen.has(m.automation_id)) out.push(m); });
    return out;
}

export const flowItem = (x) => ({ id: x.id, name: x.name || 'Flow', detail: x.description || null, steps: x.automation_ids || [] });

export function lineOf(a) {
    if (!a.active) return 'off';
    if (a.failed) return `failed ${ago(a.lastRun, { never: 'never' })}`;
    if (a.next) return `next ${until(a.next)}`;
    return a.lastRun ? `ran ${ago(a.lastRun)}` : 'never run';
}

// ── runs ─────────────────────────────────────────────────────────────────────

/** A run's raw status, said as its outcome ('Done', 'Waiting'). */
export const STATUS_WORD = STATUS_WORDS.run;
/** A raw status's dot: ok, running, quiet or failed. */
export const toneOf = (status) => (status === 'success' || status === 'completed' ? 'ok'
    : status === 'running' || status === 'pending' || status === 'queued' || status === 'dispatched' ? 'running'
        : status === 'skipped' || status === 'cancelled' ? 'quiet' : 'failed');

/** Who started a run, by an older row's trigger_source alone. */
export const triggerWord = (source) => (source ? startedByWord({ trigger_source: source }) || source : null);

// HOW LONG A RUN TOOK, in seconds, or null when there is nothing honest to say:
// a failed run stamped finished the moment it started never ran, and "0s" read
// as though it had, instantly.
function tookOf(x) {
    if (!x.started_at || !x.finished_at) return null;
    const s = Math.max(0, Math.round((new Date(x.finished_at) - new Date(x.started_at)) / 1000));
    return s === 0 && toneOf(x.status) === 'failed' ? null : s;
}

// A run's error as a sentence: no exception class in front ("RuntimeError: …",
// "BlockedURL: …"). A plain capitalised word ("Note: …") is kept.
export function errorWords(message) {
    if (!message) return null;
    const text = String(message).replace(/^(?:[A-Z][a-z]*[A-Z][A-Za-z]*|[A-Z][A-Za-z]*(?:Error|Exception)):\s*/, '').trim();
    return text ? text[0].toUpperCase() + text.slice(1) : null;
}

export function runItem(x, aut) {
    const name = x.plugin_name || aut?.plugin || x.automation_name || aut?.name || 'Run';
    return {
        id: x.id,
        name,
        automation: x.automation_name || aut?.name || null,
        automationId: x.automation_id || aut?.id || null,
        pluginId: x.plugin_id || aut?.pluginId || null,
        // which tool ran: '' (or null on an older row) is the plugin's only tool
        toolId: x.tool_id ?? aut?.toolId ?? null,
        toolName: x.tool_name || null,
        status: x.status || 'unknown',
        outcome: outcomeOf(x),
        started: x.started_at || null,
        finished: x.finished_at || null,
        error: errorWords(x.error_message),
        why: (!x.error_message && x.output_summary && typeof x.output_summary.result_status === 'string') ? x.output_summary.result_status : null,
        source: x.trigger_source || null,
        startedByKind: startedByKind(x),
        startedBy: startedByWord(x, { pluginName: name, automationName: x.automation_name || aut?.name || null }),
        // "Refused: The plugin isn't approved." — the server's words, else the stage's
        refused: x.refused?.text ? `Refused: ${x.refused.text}` : refusedLine(x.refused ? { refused_at: x.refused.stage, code: x.refused.code } : x.output_summary),
        // the realm it ran in, as its row recorded the grant
        realmId: x.trigger_context?.grant?.privacy_realm_id || x.privacy_realm_id || null,
        realmName: x.realm_name || null,
        summary: x.output_summary || null,
        // what its realm did not let it write; it ran, and left that out
        cannotWrite: x.output_summary?.cannot_write || null,
        took: tookOf(x),
    };
}

/** One page of the feed as items, with its real numbers. Throws when it could not be read. */
export async function fetchRunFeed(groupId, opts = {}) {
    const r = await runsApi.feed(groupId, opts);
    const runs = (r?.runs || []).filter((x) => x?.id).map((x) => runItem(x));
    return {
        runs,
        total: typeof r?.total === 'number' ? r.total : runs.length,
        counts: r?.counts || null,
        outcomeCounts: r?.outcome_counts || null,
    };
}

// ONE RUN by its id, with the nodes it made: { id, title, schemaName }, and
// how many there are in all. null: not there, or not one this viewer can read.
export async function readRun(groupId, runId, { limit = 50 } = {}) {
    try {
        const r = await runsApi.get(groupId, runId, { limit });
        if (!r?.run?.id) return null;
        const nodes = (r.nodes || []).filter((n) => n?.id).map((n) => ({ id: n.id, title: n.title || null, schemaName: n.schema_name || null }));
        return { ...runItem(r.run), nodes, nodesTotal: typeof r.total === 'number' ? r.total : nodes.length };
    } catch (e) {
        if (e?.status === 404 || e?.status === 403) return null;
        throw e;
    }
}

// ── plugins, read ────────────────────────────────────────────────────────────

/** Every plugin the space can see, as items. Throws when the listing failed. */
export async function fetchPlugins(groupId) {
    const r = await pluginsApi.list(groupId);
    const plugins = (r?.plugins || []).filter((p) => p?.id);
    // an older listing carries no lifecycle: those few are read in one batch,
    // and a plugin whose answer failed loses its state, not its place
    const missing = plugins.filter((p) => !p.lifecycle).map((p) => p.id);
    const statuses = missing.length ? await pluginsApi.statuses(missing, groupId).catch(() => ({})) : {};
    return plugins.map((p) => pluginItem(p, p.lifecycle || statuses?.[String(p.id)]?.lifecycle || null));
}

/** Every plugin the space can see; [] when the listing failed (for a count or a picker, not a list that says so). */
export async function loadPluginList(groupId) {
    return fetchPlugins(groupId).catch(() => []);
}

/** One plugin by its id, with its lifecycle here; null when it is not one this viewer can reach. */
export async function readPlugin(groupId, id) {
    try {
        const p = await pluginsApi.get(id);
        if (!p?.id) return null;
        const st = p.lifecycle ? null : await pluginsApi.status(id, groupId).catch(() => null);
        return pluginItem(p, p.lifecycle || st?.lifecycle || null);
    } catch (e) {
        if (e?.status === 404 || e?.status === 403) return null;
        throw e;
    }
}

/** One automation by its id; null when it is not there or not this viewer's. */
export async function readAutomation(groupId, id) {
    try {
        const r = await autosApi.get(groupId, id);
        const j = r?.automation || r;
        return j?.id ? automationItem(j) : null;
    } catch (e) {
        if (e?.status === 404 || e?.status === 403) return null;
        throw e;
    }
}

// ── waiting for you ──────────────────────────────────────────────────────────

/** Who asked for a parked run: "Planner (agent of Strava Sync)", the app, or a person (a member of a group). */
export function askedByWord(by) {
    if (!by) return 'Unnamed app';
    if (by.kind === 'person') return by.person_name || 'Someone';
    if (by.kind === 'agent' || by.agent_name) return agentWord(by.agent_name || 'Unnamed agent', by.app_name);
    return by.app_name || by.name || 'Unnamed app';
}

/** One parked run, Waiting for you (GET /plugin-requests[/<id>]). */
export function requestItem(r) {
    return {
        id: r.id,
        pluginId: r.plugin?.id || r.plugin_id || null,
        pluginName: r.plugin?.name || r.plugin_name || 'Plugin',
        toolId: r.tool?.id ?? r.tool_id ?? null,
        toolName: r.tool?.name || r.tool_name || null,
        askedBy: askedByWord(r.requested_by),
        askedByKind: r.requested_by?.kind || null,
        reason: r.consent_reason || null,
        why: consentWord(r.consent_reason, r.reason_text),
        input: r.input_parameters || {},
        realm: r.privacy_realm ? { id: r.privacy_realm.id, name: r.privacy_realm.name } : null,
        affected: num(r.affected_count),
        affectedNodes: r.affected_nodes || null,
        effects: r.effects || null,
        created: r.created_at || null,
        expires: r.expires_at || null,
        status: r.status || 'pending',
        // only a group admin may answer it
        adminOnly: !!r.admin_only,
        removeSource: !!r.remove_source,
        runId: r.run_id || null,
    };
}

// ── Tool access ──────────────────────────────────────────────────────────────

/** One connected app or agent and each tool's mode for it (GET /plugins/<id>/access):
 *  every connected one, a tool it has none of being Never. A tool's `reads` and
 *  `writes` are null when the server didn't send them (an older one). The
 *  summary is said here, in the plugin words, whatever the server's. */
export function accessItem(g) {
    const tools = (g.tools || []).map((t) => ({
        toolId: t.tool_id ?? '', name: t.name || t.tool_id || 'Tool', mode: t.mode || 'deny', word: accessWord(t.mode),
        set: t.set || 'default', irreversible: !!t.irreversible,
        reads: Array.isArray(t.reads) ? t.reads : null,
        writes: Array.isArray(t.writes) ? t.writes : null,
    }));
    const appName = g.app?.name || 'An app';
    return {
        epId: g.ep_id,
        kind: g.kind || (g.agent ? 'agent' : 'app'),
        app: g.app || null,
        agent: g.agent || null,
        appName,
        name: g.agent?.name ? agentWord(g.agent.name, appName) : appName,
        realm: g.realm || null,
        tools,
        summary: accessSummary(tools),
    };
}
